/**
 * Feature Flags React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useI18n } from '@/i18n';
import { featureFlagsApi } from '@/lib/api/featureFlags';
import type {
  CreateFlagRequest,
  UpdateFlagRequest,
  TransitionRequest,
  CreateOverrideRequest,
} from '@/types/featureFlags';

const QUERY_KEY = ['feature-flags'];

export function useFeatureFlags(params?: {
  flag_type?: string;
  lifecycle_state?: string;
  owner?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: [...QUERY_KEY, params],
    queryFn: () => featureFlagsApi.listFlags(params),
  });
}

export function useFlagOverrides(flagKey: string) {
  return useQuery({
    queryKey: [...QUERY_KEY, flagKey, 'overrides'],
    queryFn: () => featureFlagsApi.listOverrides(flagKey),
    enabled: !!flagKey,
  });
}

export function useAuditLogs(flagKey: string, limit = 50, offset = 0) {
  return useQuery({
    queryKey: [...QUERY_KEY, flagKey, 'audit', limit, offset],
    queryFn: () => featureFlagsApi.getAuditLogs(flagKey, limit, offset),
    enabled: !!flagKey,
  });
}

export function useCreateFlag() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: (data: CreateFlagRequest) => featureFlagsApi.createFlag(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) => {
      toast.error(`${t('featureFlags.toasts.createFailed')}: ${err.message}`);
    },
  });
}

export function useUpdateFlag() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: ({ flagKey, data }: { flagKey: string; data: UpdateFlagRequest }) =>
      featureFlagsApi.updateFlag(flagKey, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) => {
      toast.error(`${t('featureFlags.toasts.saveFailed')}: ${err.message}`);
    },
  });
}

export function useTransitionFlag() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: ({ flagKey, data }: { flagKey: string; data: TransitionRequest }) =>
      featureFlagsApi.transitionFlag(flagKey, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) => {
      toast.error(`${t('featureFlags.toasts.toggleFailed')}: ${err.message}`);
    },
  });
}

export function useDeleteFlag() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: (flagKey: string) => featureFlagsApi.deleteFlag(flagKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) => {
      toast.error(`${t('featureFlags.toasts.deleteFailed')}: ${err.message}`);
    },
  });
}

export function useCreateOverride() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: ({ flagKey, data }: { flagKey: string; data: CreateOverrideRequest }) =>
      featureFlagsApi.createOverride(flagKey, data),
    onSuccess: (_data, { flagKey }) => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, flagKey, 'overrides'] });
    },
    onError: (err: Error) => {
      toast.error(`${t('featureFlags.toasts.overrideSetFailed')}: ${err.message}`);
    },
  });
}

export function useDeleteOverride() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: ({ flagKey, userId }: { flagKey: string; userId: string }) =>
      featureFlagsApi.deleteOverride(flagKey, userId),
    onSuccess: (_data, { flagKey }) => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, flagKey, 'overrides'] });
    },
    onError: (err: Error) => {
      toast.error(`${t('featureFlags.toasts.overrideDeleteFailed')}: ${err.message}`);
    },
  });
}
